(() => {
  const initCarousel = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const track = root.querySelector('[data-fw-carousel-track]');
    const slides = Array.from(root.querySelectorAll('[data-fw-carousel-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-fw-carousel-dot]'));
    const previous = root.querySelector('[data-fw-carousel-previous]');
    const next = root.querySelector('[data-fw-carousel-next]');
    if (!track || slides.length === 0) return;
    root.dataset.fwReady = 'true';
    let active = 0;
    let timer;
    const update = () => {
      const left = track.scrollLeft;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const current = Math.abs(slide.offsetLeft - left);
        if (current < distance) {
          distance = current;
          nearest = index;
        }
      });
      active = nearest;
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
    };
    const go = (index) => {
      const target = (index + slides.length) % slides.length;
      track.scrollTo({ left: slides[target].offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    previous?.addEventListener('click', () => go(active - 1));
    next?.addEventListener('click', () => go(active + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => go(index)));
    track.addEventListener('scroll', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(update, 70);
    }, { passive: true });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') go(active - 1);
      if (event.key === 'ArrowRight') go(active + 1);
    });
    update();
  };

  const initTabs = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const tabs = Array.from(root.querySelectorAll('[data-fw-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-fw-tab-panel]'));
    if (!tabs.length) return;
    root.dataset.fwReady = 'true';
    const select = (index, focus = false) => {
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (focus && selected) tab.focus();
      });
      panels.forEach((panel, panelIndex) => {
        const selected = panelIndex === index;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
      });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index));
      tab.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') select((index - 1 + tabs.length) % tabs.length, true);
        if (event.key === 'ArrowRight') select((index + 1) % tabs.length, true);
      });
    });
    select(0);
  };

  const initHotspots = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const buttons = Array.from(root.querySelectorAll('[data-fw-hotspot-button]'));
    const panels = Array.from(root.querySelectorAll('[data-fw-hotspot-panel]'));
    if (!buttons.length) return;
    root.dataset.fwReady = 'true';
    const select = (id) => {
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.fwHotspotButton === id)));
      panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.fwHotspotPanel === id));
    };
    buttons.forEach((button) => button.addEventListener('click', () => select(button.dataset.fwHotspotButton)));
    select(buttons[0].dataset.fwHotspotButton);
  };

  const initBeforeAfter = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const range = root.querySelector('[data-fw-before-after-range]');
    if (!range) return;
    root.dataset.fwReady = 'true';
    const update = () => root.style.setProperty('--fw-a-split', `${range.value}%`);
    range.addEventListener('input', update);
    update();
  };

  const initSpin = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const frames = Array.from(root.querySelectorAll('[data-fw-spin-frame]'));
    if (!frames.length) return;
    root.dataset.fwReady = 'true';
    let active = 0;
    let startX = null;
    let startIndex = 0;
    const show = (index) => {
      active = (index + frames.length) % frames.length;
      frames.forEach((frame, frameIndex) => frame.classList.toggle('is-active', frameIndex === active));
    };
    const begin = (event) => {
      startX = event.clientX;
      startIndex = active;
      root.setPointerCapture?.(event.pointerId);
    };
    const move = (event) => {
      if (startX === null) return;
      const step = Math.round((event.clientX - startX) / 12);
      show(startIndex - step);
    };
    const end = () => { startX = null; };
    root.addEventListener('pointerdown', begin);
    root.addEventListener('pointermove', move);
    root.addEventListener('pointerup', end);
    root.addEventListener('pointercancel', end);
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(active - 1);
      if (event.key === 'ArrowRight') show(active + 1);
    });
    show(0);
  };

  const initRoi = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const rental = root.querySelector('[data-fw-roi-rental]');
    const trips = root.querySelector('[data-fw-roi-trips]');
    const months = root.querySelector('[data-fw-roi-months]');
    const product = root.querySelector('[data-fw-roi-product]');
    const output = root.querySelector('[data-fw-roi-output]');
    if (!rental || !trips || !months || !product || !output) return;
    root.dataset.fwReady = 'true';
    const update = () => {
      const alternative = Number(rental.value || 0) * Number(trips.value || 0) * Number(months.value || 0);
      const difference = alternative - Number(product.value || 0);
      output.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(difference);
    };
    [rental, trips, months, product].forEach((input) => input.addEventListener('input', update));
    update();
  };

  const initFit = (root) => {
    if (root.dataset.fwReady === 'true') return;
    const good = Array.from(root.querySelectorAll('[data-fw-fit-good]'));
    const consider = Array.from(root.querySelectorAll('[data-fw-fit-consider]'));
    const result = root.querySelector('[data-fw-fit-result]');
    if (!result) return;
    root.dataset.fwReady = 'true';
    const initial = result.textContent;
    const update = () => {
      const goodCount = good.filter((input) => input.checked).length;
      const considerCount = consider.filter((input) => input.checked).length;
      if (considerCount > 0) result.textContent = root.dataset.fwFitConsider;
      else if (goodCount > 0) result.textContent = root.dataset.fwFitPositive;
      else result.textContent = initial;
    };
    [...good, ...consider].forEach((input) => input.addEventListener('change', update));
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-fw-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-fw-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-fw-hotspots]').forEach(initHotspots);
    scope.querySelectorAll('[data-fw-before-after]').forEach(initBeforeAfter);
    scope.querySelectorAll('[data-fw-spin]').forEach(initSpin);
    scope.querySelectorAll('[data-fw-roi]').forEach(initRoi);
    scope.querySelectorAll('[data-fw-fit]').forEach(initFit);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
